import { ref } from 'vue';
import { defineStore } from 'pinia';
import useAxiosInstance from '@/composables/axiosInstance';
import { Department, DepartmentResponse } from '@/interfaces/CBW/DepartmentResponse';
import { EmployeeResponse, EmployeeBasicDetails } from '@/interfaces/CBW/EmployeeResponse';
import { CompanyResponse } from '@/interfaces/CBW/CompanyResponse';
import { CompanyExternalDbSettingResponse } from '@/interfaces/CBW/CompanyExternalDbSettingResponse';
import { EmployeeLinkedRecordsResponse } from '@/interfaces/CBW/EmployeeLinkedRecordsResponse';
import useFlashMessageStore from '@/stores/flash-messages';
import useUISessionStore from '../ui-session';
import { EmployeePayDetailsResponse } from '@/interfaces/CBW/EmployeePayDetailsResponse';

interface State {
  departments : Department[];
  employees : EmployeeBasicDetails[];
  employee : EmployeeResponse | null;
  company : CompanyResponse | null;
  companyExternalDbSetting : CompanyExternalDbSettingResponse | null;
  employeeLinkedRecords : EmployeeLinkedRecordsResponse | null;
  includeTerminatedEmployees : boolean;
  employeePayDetails : EmployeePayDetailsResponse | null;
  newEmpCode : string;
}

interface DeleteResponse {
  error : string;
}

interface ChangeEmpCodeResponse {
  error : string;
  message : string;
}

interface EmployeeDataTransferResponse {
  error : string;
}

const useEmployeeManagementActionsStore = defineStore({
  id: 'EmployeeManagementActions',
  state: () => ref<State>({
    departments: [],
    employees: [],
    employee: null,
    company: null,
    companyExternalDbSetting: null,
    employeeLinkedRecords: null,
    includeTerminatedEmployees: false,
    employeePayDetails: null,
    newEmpCode: '',
  }),
  getters: {
    employeeFullName(state) {
      return `${state.departments[0].description}`;
    },
    employeeList(state) {
      return state.employees.filter((employee) => {
        if (state.includeTerminatedEmployees) {
          return true;
        }
        return employee.terminationDate === null;
      }).map((employee) => ({
        ...employee,
        labelText: `${(employee.preferredName ?? employee.givenName)} ${employee.surname} (${employee.empCode})`,
      })).sort((a, b) => (a.labelText > b.labelText ? 1 : -1));
    },
  },
  actions: {
    async init(employeeCode : string) {
      await this.fetchMyEmployeeDetails(employeeCode);
      await this.fetchMyEmployeePayDetails();
      await this.fetchMyCompanyDetails();
      await this.fetchCompanyExternalDbSettingResponseDetails();
    },
    clearEmployeeDetails() {
      this.$state.employee = null;
      this.$state.newEmpCode = '';
    },
    async delete() : Promise<DeleteResponse | void> {
      try {
        const uiSessionStore = useUISessionStore();
        await useAxiosInstance.post('/api/Employees/DeleteAndUpdateLinkedRecords', {
          empCode: this.$state.employee?.empCode,
          adminEmpCode: uiSessionStore.user.empCode,
        });
      } catch (error : any) {
        console.error(error);
        const messageStore = useFlashMessageStore();
        if (error.response.data.Error && error.response.data.Error.length) {
          messageStore.pushNotification({
            id: 'DELETE_ERROR',
            type: 'error',
            message: error.response.data.Error[0],
          });
          return {
            error: error.response.data.Error[0],
          };
        }
        messageStore.pushNotification({
          id: error.code,
          type: 'error',
          message: error.message,
        });
        return {
          error: error.message,
        };
      }
    },
    async changeEmpCode(newEmpCode : string) : Promise<ChangeEmpCodeResponse | void> {
      this.$state = {
        ...this.$state,
        newEmpCode,
      };
      try {
        const uiSessionStore = useUISessionStore();
        await useAxiosInstance.post('/api/Employees/ChangeEmpCode', {
          oldEmpCode: this.$state.employee?.empCode,
          newEmpCode,
          loggedInEmpCode: uiSessionStore.user.empCode
        });
      } catch (error : any) {
        console.error(error);
        const messageStore = useFlashMessageStore();
        if (error.response.data.Error && error.response.data.Error.length) {
          messageStore.pushNotification({
            id: 'CHANGE_EMP_CODE_ERROR',
            type: 'error',
            message: error.response.data.Error[0],
          });
          return {
            error: error.response.data.Error[0],
            message: error.response.data,
          };
        }
        messageStore.pushNotification({
          id: error.code,
          type: 'error',
          message: error.message,
        });
        return {
          error: error.message,
          message: error.response.data,
        };
      }
    },
    async mergeEmpCodes(mergeToEmployeeCode : string) : Promise<EmployeeDataTransferResponse | void> {
      try {
        const sessionStore = useUISessionStore();
        await useAxiosInstance.post('/api/Employees/Merge', {
          oldEmpCode: this.$state.employee?.empCode,
          newEmpCode: mergeToEmployeeCode,
          loggedInEmpCode: sessionStore.user.empCode
        });
      } catch (error : any) {
        console.error(error);
        const messageStore = useFlashMessageStore();
        if (error.response.data.Error && error.response.data.Error.length) {
          messageStore.pushNotification({
            id: 'MERGE_EMPCODES_ERROR',
            type: 'error',
            message: error.response.data.Error[0],
          });
          return {
            error: error.response.data.Error[0],
          };
        }
        messageStore.pushNotification({
          id: error.code,
          type: 'error',
          message: error.message,
        });
        return {
          error: error.message,
        };
      }
    },
    async fetchAllDepartments() : Promise<void> {
      try {
        // Will only return top 20 due to pagination being ignored
        const { data } = await useAxiosInstance.get<DepartmentResponse>('/Department/GetAllDepartments');

        this.$state = {
          ...this.$state,
          departments: data.results,
        };
      } catch (error : any) {
        console.error(error);
      }
    },
    async fetchInactiveEmployees() : Promise<void> {
      if (this.$state.employee) {
        try {
          const { data } = await useAxiosInstance.get<EmployeeBasicDetails[]>(`/api/Employees?CompanyId=${this.$state.employee.companyId}&IsEnabled=0`);
          this.$state = {
            ...this.$state,
            employees: data,
          };
        } catch (error : any) {
          console.error(error);
        }
      }
    },
    async fetchEmployees() : Promise<void> {
      if (this.$state.employee) {
        try {
          const { data } = await useAxiosInstance.get<EmployeeBasicDetails[]>(`/api/Employees?CompanyId=${this.$state.employee.companyId}`);
          this.$state = {
            ...this.$state,
            employees: data,
          };
        } catch (error : any) {
          console.error(error);
        }
      }
    },    
    async fetchMyEmployeeDetails(employeeCode : string) : Promise<void> {
      try {
        const { data } = await useAxiosInstance.get<EmployeeResponse>(`/api/Employees/${employeeCode}`);

        this.$state = {
          ...this.$state,
          employee: data,
        };
      } catch (error : any) {
        console.error(error);
      }
    },
    async fetchEmployeeDetails(employeeCode : string) : Promise<EmployeeResponse | undefined> {
      try {
        const { data } = await useAxiosInstance.get<EmployeeResponse>(`/api/Employees/${employeeCode}`);

        return data;
      } catch (error : any) {
        console.error(error);

        return undefined;
      }
    },
    async fetchMyCompanyDetails() : Promise<void> {
      if (this.$state.employee) {
        try {
          const { data } = await useAxiosInstance.get<CompanyResponse>(`/api/Companies/${this.$state.employee.companyId}`);

          this.$state = {
            ...this.$state,
            company: data,
          };
        } catch (error : any) {
          console.error(error);
        }
      }
    },
    async fetchCompanyDetails(companyId : Number) : Promise<CompanyResponse | undefined> {
      try {
        const { data } = await useAxiosInstance.get<CompanyResponse>(`/api/Companies/${companyId}`);

        return data;
      } catch (error : any) {
        console.error(error);

        return undefined;
      }
    },
    async fetchMyEmployeePayDetails() : Promise<void> {
      if (this.$state.employee) {
        try {
          const { data } = await useAxiosInstance.get<EmployeePayDetailsResponse>(`/api/EmployeePayDetails/${this.$state.employee.empCode}`);

          this.$state = {
            ...this.$state,
            employeePayDetails: data,
          };
        } catch (error : any) {
          console.error(error);
        }
      }
    },
    async fetchEmployeePayDetails(empCode : string) : Promise<EmployeePayDetailsResponse | undefined> {
      try {
        const { data } = await useAxiosInstance.get<EmployeePayDetailsResponse>(`/api/EmployeePayDetails/${empCode}`);

        return data;
      } catch (error : any) {
        console.error(error);

        return undefined;
      }
    },
    async fetchCompanyExternalDbSettingResponseDetails() : Promise<void> {
      if (this.$state.employee) {
        try {
          const { data } = await useAxiosInstance.get<CompanyExternalDbSettingResponse>(`/api/CompanyExternalDbSettings/${this.$state.employee.companyId}`);

          this.$state = {
            ...this.$state,
            companyExternalDbSetting: data,
          };
        } catch (error : any) {
          console.error(error);
        }
      }
    },
    async fetchEmployeeLinkedRecordsResponseDetails() : Promise<void> {
      if (this.$state.employee) {
        try {
          const { data } = await useAxiosInstance.get<EmployeeLinkedRecordsResponse>(`/api/Employees/${this.$state.employee.empCode}/LinkedRecords`);

          this.$state = {
            ...this.$state,
            employeeLinkedRecords: data,
          };
        } catch (error : any) {
          console.error(error);
        }
      }
    },
    async validateEmployeeCodeExists(employeeCode : string) : Promise<boolean> {
      try {
        const response = await useAxiosInstance.get<EmployeeResponse>(`/api/Employees/${employeeCode}`);

        return response.status === 200;
      } catch (error : any) {
        console.error(error);
        return true;
      }
    },
  },
});

export default useEmployeeManagementActionsStore;
